import { DEFAULT_DATETIME_TIME } from "@patient-watch/forms";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";

dayjs.extend(utc);

/** Parse a UTC wall-clock value from draft input or ISO strings. */
export function parseDraftUtc(value: string) {
    if (!value) {
        return dayjs.utc(NaN);
    }

    if (value.endsWith("Z") || /[+-]\d{2}:\d{2}$/.test(value)) {
        return dayjs.utc(value);
    }

    const [datePart, timePart = "00:00"] = value.split("T");
    if (!datePart) {
        return dayjs.utc(NaN);
    }

    const [year = 0, month = 1, day = 1] = datePart.split("-").map(Number);
    const [hours = 0, minutes = 0] = timePart.split(":").map(Number);

    return dayjs
        .utc()
        .year(year)
        .month(month - 1)
        .date(day)
        .hour(hours)
        .minute(minutes)
        .second(0)
        .millisecond(0);
}

export function isoToDraftDateTime(iso: string): string {
    return parseDraftUtc(iso).format("YYYY-MM-DDTHH:mm");
}

export function draftDateTimeToIso(value: string): string {
    return parseDraftUtc(value).toISOString();
}

export function draftDateTimeToIsoEndOfMinute(value: string): string {
    return parseDraftUtc(value).endOf("minute").toISOString();
}

export function draftDateTimeToParts(value: string): { date: Date | null; time: string } {
    if (!value) {
        return { date: null, time: "" };
    }

    const parsed = parseDraftUtc(value);
    if (!parsed.isValid()) {
        return { date: null, time: "" };
    }

    return {
        date: new Date(parsed.year(), parsed.month(), parsed.date()),
        time: parsed.format("HH:mm"),
    };
}

export function partsToDraftDateTime(date: Date | null, time: string): string {
    if (!date && !time) {
        return "";
    }

    const base = date
        ? dayjs.utc().year(date.getFullYear()).month(date.getMonth()).date(date.getDate())
        : dayjs.utc().startOf("day");
    const [hours = "10", minutes = "00"] = (time || DEFAULT_DATETIME_TIME).split(":");

    return base.hour(Number(hours)).minute(Number(minutes)).format("YYYY-MM-DDTHH:mm");
}

/** Map UTC ISO to a local-form ISO for DateTime (wall-clock UTC components). */
export function utcIsoToLocalFormIso(iso: string | null | undefined): string | null {
    if (!iso) return null;

    const parsed = parseDraftUtc(iso);
    if (!parsed.isValid()) return null;

    return dayjs()
        .year(parsed.year())
        .month(parsed.month())
        .date(parsed.date())
        .hour(parsed.hour())
        .minute(parsed.minute())
        .second(0)
        .millisecond(0)
        .toISOString();
}

/** Map DateTime local-form ISO back to UTC ISO for URL and RPC. */
export function localFormIsoToUtcIso(iso: string | null | undefined): string | undefined {
    if (!iso) return undefined;

    const local = dayjs(iso);
    if (!local.isValid()) return undefined;

    return dayjs
        .utc()
        .year(local.year())
        .month(local.month())
        .date(local.date())
        .hour(local.hour())
        .minute(local.minute())
        .second(0)
        .millisecond(0)
        .toISOString();
}
